import React, { useState, useEffect } from 'react';
import { Search, Plus, Calendar, ImageIcon, Camera, AlertCircle, RefreshCw, Ban, Eye, MessageSquare, Share2, Filter, X } from 'lucide-react';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import ImageGallery from '../ui/ImageGallery';
import LoadMoreButton from '../ui/LoadMoreButton';
import { attendanceService } from '../../services';
import apiClient from '../../services/apiClient';
import { PermissionGuard } from '../auth/PermissionGuard';
import { formatHours } from '../../utils/helpers';
import { ButtonPermissions } from '../../types/permission';

const HomeView = ({ onRecordClick, onTokenAccess, onSearch, onAddRecord, searchParams = {}, searchTrigger = 0, onStudentFilter, activeStudentFilter }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const sentinelRef = React.useRef(null); // 滚动加载哨兵


  // 加载上课记录
  const loadRecords = async (pageNum = 1, append = false) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pageNum,
        per_page: 5,
        ...searchParams, // 合并搜索参数
      };

      // 状态筛选逻辑：
      // 1. 如果显式选择 'all'，则不传 status (后端默认返回全部)
      // 2. 如果未指定 status (初始加载)，则默认显示 'ACTIVE'
      // 3. 否则按指定的 status 过滤
      if (params.status === 'all') {
        delete params.status;
      } else if (!params.status) {
        params.status = 'ACTIVE';
      }

      const response = await attendanceService.getList(params);

      const items = response.items || [];

      if (append) {
        setRecords(prev => [...prev, ...items]);
      } else {
        setRecords(items);
      }

      setHasMore(response.has_next || false);
      setPage(pageNum);
    } catch (err) {
      console.error('加载上课记录失败:', err);
      setError(err.message || '加载失败');
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  // 初始加载
  useEffect(() => {
    loadRecords();
  }, []);

  // 搜索触发时重新加载（包括选择"全部"）
  useEffect(() => {
    if (searchTrigger > 0) {
      loadRecords(1);
    }
  }, [searchTrigger]);

  // 加载更多
  const handleLoadMore = () => {
    if (!loading && !isLoadingMore && hasMore) {
      setIsLoadingMore(true);
      loadRecords(page + 1, true);
    }
  };

  // 使用 IntersectionObserver 监听滚动到底部
  useEffect(() => {
    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && !isLoadingMore && !loading && hasMore && !error) {
          console.log('📥 管理端触发加载更多 (IntersectionObserver)');
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinelRef.current);

    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
      }
    };
  }, [hasMore, isLoadingMore, loading, page]);

  return (
    <div className="flex flex-col h-full bg-[#F7FAFC]">
      {/* Header */}
      <header className="h-[48px] px-4 bg-white flex items-center justify-between border-b border-[#EDF2F7] flex-shrink-0">
        <h1 className="text-base font-bold text-[#2D3748]">上课记录</h1>
        <div className="flex items-center gap-2">
          <Button
            variant="icon"
            icon={RefreshCw}
            onClick={() => loadRecords(1)}
            disabled={loading}
            className={loading ? 'animate-spin' : ''}
          />
          <Button variant="icon" icon={Search} onClick={() => onSearch('record')} />
          <PermissionGuard permission={ButtonPermissions.attendance.create}>
            <Button variant="primary" size="small" icon={Plus} onClick={onAddRecord}>
              新增
            </Button>
          </PermissionGuard>
        </div>
      </header>

      {/* 学生筛选激活提示条 */}
      {activeStudentFilter && (
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border-b border-blue-100 flex-shrink-0">
          <Filter size={14} className="text-blue-500 flex-shrink-0" />
          <span className="text-xs text-blue-700 flex-1">
            正在查看 <strong>{activeStudentFilter}</strong> 的全部记录
          </span>
          <button
            onClick={() => onStudentFilter && onStudentFilter(null)}
            className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 hover:bg-blue-100 px-2 py-0.5 rounded-full transition-colors"
          >
            <X size={12} />
            清除
          </button>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 overflow-y-auto px-4 pt-4 pb-24 space-y-3">
        {/* 错误提示 */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-700 font-medium">加载失败</p>
              <p className="text-xs text-red-600 mt-1">{error}</p>
              <button
                onClick={() => {
                  apiClient.resetCircuit();
                  loadRecords(1);
                }}
                className="mt-2 text-xs text-red-700 underline"
              >
                重试
              </button>
            </div>
          </div>
        )}

        {/* 加载中 */}
        {loading && records.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <RefreshCw size={32} className="text-[#3182CE] animate-spin mb-3" />
            <p className="text-sm text-[#718096]">加载中...</p>
          </div>
        )}

        {/* 记录列表 */}
        {records.map((record, idx) => {
          // 格式化日期，只显示年月日
          const formatDate = (dateStr) => {
            if (!dateStr) return '未知日期';
            // 如果包含时间，只取日期部分
            return dateStr.split(' ')[0];
          };

          // 计算总图片数
          const totalImages =
            (record.session_artwork_photos_urls?.length || 0) +
            (record.classroom_photos_urls?.length || 0);

          return (
            <Card key={record.id} className={`overflow-hidden p-3 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'} ${record.status === 'VOIDED' ? 'opacity-60 bg-gray-50' : ''}`}>
              <div>
                <div className="flex justify-between items-start mb-1.5">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <h3
                        className={`font-bold text-[15px] truncate max-w-[120px] transition-colors ${
                          onStudentFilter
                            ? 'text-[#3182CE] cursor-pointer hover:text-[#2563EB] active:text-[#1D4ED8] underline decoration-dotted'
                            : 'text-[#2D3748]'
                        }`}
                        onClick={(e) => {
                          if (!onStudentFilter) return;
                          e.stopPropagation();
                          onStudentFilter(record.student_name);
                        }}
                        title={onStudentFilter ? `点击筛选 ${record.student_name} 的全部记录` : undefined}
                      >
                        {record.student_name || '未知学员'}
                      </h3>
                      


                      <Badge color="blue" className="text-[11px] px-2 py-0.5">{(record.course_name || '未知').substring(0, 10)}</Badge>
                      {record.status === 'VOIDED' && (
                        <Badge color="red" className="flex items-center gap-1 text-[11px] px-2 py-0.5">
                          <Ban size={10} />
                          作废
                        </Badge>
                      )}
                    </div>
                    {/* 合并一行：日期、课时、图片数量 */}
                    <div className="text-xs flex items-center gap-2 flex-wrap">
                      <span className="flex items-center gap-1 text-[#718096]">
                        <Calendar size={10} /> {formatDate(record.date)}
                      </span>
                      {record.remaining_hours !== undefined && (
                        <span className="px-2 py-0.5 bg-green-50 text-green-600 rounded-md font-bold">
                          剩{formatHours(record.remaining_hours)}
                        </span>
                      )}
                      {record.session_artwork_photos_urls && record.session_artwork_photos_urls.length > 0 && (
                        <span className="px-2 py-0.5 bg-purple-50 text-purple-600 rounded-md font-bold">
                          🎨{record.session_artwork_photos_urls.length}
                        </span>
                      )}
                      {record.classroom_photos_urls && record.classroom_photos_urls.length > 0 && (
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-md font-bold">
                          📸{record.classroom_photos_urls.length}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* 操作按钮 */}
                  <div className="flex gap-1 -mt-1 -mr-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onTokenAccess({ id: record.student_id, name: record.student_name });
                      }}
                      className="p-1 text-[#3182CE] hover:bg-blue-50 rounded-lg transition-colors"
                      title="分享链接给家长"
                    >
                      <Share2 size={16} />
                    </button>
                    <button
                      onClick={() => onRecordClick(record)}
                      className="p-1 text-[#3182CE] hover:bg-blue-50 rounded-lg transition-colors"
                      title="查看记录详情"
                    >
                      <Eye size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* 直接展示图片画廊 */}
              {totalImages > 0 && (
                <div className="border-t border-[#EDF2F7] bg-gray-50">
                  <ImageGallery
                    artworkPhotos={record.session_artwork_photos_urls || []}
                    classroomPhotos={record.classroom_photos_urls || []}
                    showThumbnails={true}
                    thumbnailSize="small"
                    showNavigationArrows={false}
                    hideOnError={true}
                  />
                </div>
              )}

              {/* 老师点评 - 现在放在图片下面 */}
              {record.remarks && (
                <div className={`p-2.5 ${totalImages > 0 ? 'border-t border-gray-100/50' : 'mt-2 pt-1.5 border-t border-gray-100/50'} bg-white`}>
                  <div className="flex gap-1.5">
                    <MessageSquare size={14} className="text-blue-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-[#4A5568] leading-relaxed break-words flex-1 italic">
                      {record.remarks}
                    </p>
                  </div>
                </div>
              )}
            </Card>
          );
        })}

        {/* 空状态 */}
        {!loading && !error && records.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <Calendar size={48} className="text-gray-300 mb-3" />
            <p className="text-sm text-[#718096]">暂无上课记录</p>
            <PermissionGuard permission={ButtonPermissions.attendance.create}>
              <button
                onClick={onAddRecord}
                className="mt-3 text-xs text-[#3182CE] underline"
              >
                添加第一条记录
              </button>
            </PermissionGuard>
          </div>
        )}

        {/* 加载更多哨兵 */}
        <div ref={sentinelRef} className="h-4 w-full" />

        {/* 加载分页 */}
        <div className="pb-20">
          <LoadMoreButton 
            onClick={handleLoadMore} 
            loading={loading || isLoadingMore} 
            hasMore={hasMore && records.length > 0} 
          />
        </div>
      </main>
    </div>
  );
};

export default HomeView;
